/**
 * Life4Earth V2 — Project Risks API Adapter
 *
 * CRUD + materialize for the Risk Register.
 *
 * Endpoint: /api/projects/{guid}/risks
 *
 * Epic: DEV-683
 */

import { apiFetch } from "./client";

// ── Types ─────────────────────────────────────────────────────

export type RiskStatus = "identified" | "mitigating" | "accepted" | "closed";
export type RiskCategory =
  | "technical"
  | "operational"
  | "financial"
  | "environmental"
  | "regulatory"
  | "reputational"
  | "safety";

export interface ProjectRiskResponse {
  id: string;
  projectGuid: string;
  title: string;
  description: string | null;
  category: string;
  likelihood: number; // 1–5
  impact: number; // 1–5
  riskScore: number; // likelihood × impact
  mitigationPlan: string | null;
  triggerConditions: string | null;
  ownerRoleSlotGuid: string | null;
  ownerUserGuid: string | null;
  status: string;
  isKeyRisk: boolean;
  linkedTaskIds: string[];
  sourceFlagId: string | null;
  materializedIssueId: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectRiskRequest {
  title: string;
  description?: string;
  category?: string;
  likelihood: number;
  impact: number;
  mitigationPlan?: string;
  triggerConditions?: string;
  ownerRoleSlotGuid?: string;
  linkedTaskIds?: string[];
  sourceFlagId?: string;
}

export interface UpdateProjectRiskRequest {
  title?: string;
  description?: string;
  category?: string;
  likelihood?: number;
  impact?: number;
  mitigationPlan?: string;
  triggerConditions?: string;
  ownerRoleSlotGuid?: string;
  status?: string;
  isKeyRisk?: boolean;
  linkedTaskIds?: string[];
}

// ── API Functions ─────────────────────────────────────────────

/** Fetch all risks for a project. */
export async function fetchProjectRisks(
  projectGuid: string
): Promise<ProjectRiskResponse[]> {
  return apiFetch<ProjectRiskResponse[]>(
    `/api/projects/${projectGuid}/risks`
  );
}

/** Fetch a single risk by ID. */
export async function fetchProjectRisk(
  projectGuid: string,
  riskId: string
): Promise<ProjectRiskResponse> {
  return apiFetch<ProjectRiskResponse>(
    `/api/projects/${projectGuid}/risks/${riskId}`
  );
}

/** Create a new risk. */
export async function createProjectRisk(
  projectGuid: string,
  req: CreateProjectRiskRequest
): Promise<ProjectRiskResponse> {
  return apiFetch<ProjectRiskResponse>(
    `/api/projects/${projectGuid}/risks`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/** Patch-update a risk. */
export async function updateProjectRisk(
  projectGuid: string,
  riskId: string,
  req: UpdateProjectRiskRequest
): Promise<ProjectRiskResponse> {
  return apiFetch<ProjectRiskResponse>(
    `/api/projects/${projectGuid}/risks/${riskId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/** Delete a risk. */
export async function deleteProjectRisk(
  projectGuid: string,
  riskId: string
): Promise<void> {
  await apiFetch<void>(
    `/api/projects/${projectGuid}/risks/${riskId}`,
    { method: "DELETE" }
  );
}

/** Response envelope from the materialize endpoint. */
export interface MaterializeRiskResult {
  risk: ProjectRiskResponse;
  issue: { id: string; [key: string]: unknown };
}

/** Materialize a risk into an issue. */
export async function materializeRisk(
  projectGuid: string,
  riskId: string
): Promise<MaterializeRiskResult> {
  return apiFetch<MaterializeRiskResult>(
    `/api/projects/${projectGuid}/risks/${riskId}/materialize`,
    { method: "POST" }
  );
}
